import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { getClassLogEditRequests, reviewClassLogEditRequest } from '../../lib/api';
import type { ClassLogEditRequest, ClassLogEditStatus, ClassLogRequestType } from '../../lib/api';

const FIELD_LABELS: Record<string, string> = {
  date: 'Date', attendance: 'Attendance', startTime: 'Start Time', endTime: 'End Time',
  topic: 'Topic / Task', portion: 'Portion', remark: 'Remark',
};

const STATUS_STYLE: Record<ClassLogEditStatus, { bg: string; color: string }> = {
  Pending:  { bg: '#fef3c7', color: '#92400e' },
  Approved: { bg: '#dcfce7', color: '#15803d' },
  Rejected: { bg: '#fee2e2', color: '#991b1b' },
};

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

// ── Main Page ─────────────────────────────────────────────────────────────────

const ClassLogEdits: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [filter, setFilter] = useState<ClassLogEditStatus | 'All'>('Pending');
  const [typeFilter, setTypeFilter] = useState<ClassLogRequestType | 'All'>('All');
  const [requests, setRequests] = useState<ClassLogEditRequest[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClassLogEditRequests(adminPassword, filter, typeFilter);
      setRequests(res.requests ?? []);
      setPendingCount(res.pendingCount ?? 0);
    } catch (err: any) { toast.error('Failed to load: ' + err.message); }
    finally { setLoading(false); }
  }, [adminPassword, filter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const review = async (req: ClassLogEditRequest, decision: 'Approved' | 'Rejected') => {
    const isDelete = req.type === 'delete';
    if (isDelete && decision === 'Approved' &&
        !window.confirm(`Permanently delete ${req.studentName}'s class on ${req.classDate}?`)) return;
    setBusy(req._id);
    try {
      await reviewClassLogEditRequest(adminPassword, req._id, decision, notes[req._id] ?? '');
      toast.success(decision === 'Rejected'
        ? `Rejected — ${req.studentName}'s class log is unchanged`
        : isDelete ? `Approved — ${req.studentName}'s class log is deleted`
        : `Approved — ${req.studentName}'s class log is updated`);
      load();
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  return (
    <TrackerLayout>
      <div className="space-y-5">
        <div className="rounded-2xl overflow-hidden shadow-lg"
          style={{ background: 'linear-gradient(135deg,#2b2240 0%,#3d2f5f 50%,#2b2240 100%)' }}>
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">📝</span>
                <h1 className="text-xl font-bold text-white">Log Edit Requests</h1>
              </div>
              <p className="text-white/50 text-xs">Edits and deletions mentors and coordinators asked for — a class log only changes when you approve</p>
            </div>
            <button onClick={load}
              className="self-start sm:self-auto px-3 py-2 rounded-xl text-xs font-medium border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
              ↻ Refresh
            </button>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {(['Pending', 'Approved', 'Rejected', 'All'] as const).map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className="text-xs font-semibold px-4 py-2 rounded-xl border-2 transition-colors"
              style={filter === s
                ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }}>
              {s}{s === 'Pending' && pendingCount > 0 ? ` (${pendingCount})` : ''}
            </button>
          ))}
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 sm:ml-auto">
            {([['All', 'All types'], ['edit', '✏️ Edits'], ['delete', '🗑 Deletes']] as const).map(([t, label]) => (
              <button key={t} onClick={() => setTypeFilter(t)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all"
                style={typeFilter === t ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-8 h-8 rounded-full border-3 border-purple-200 border-t-purple-600 animate-spin" style={{ borderWidth: 3 }} />
            <p className="text-sm text-gray-400">Loading requests…</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-14 text-center">
            <div className="text-5xl mb-3">✅</div>
            <p className="text-gray-700 font-bold text-base">{filter === 'Pending' ? 'No requests waiting for approval' : 'Nothing here yet'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map(req => {
              const st = STATUS_STYLE[req.status];
              const isPending = req.status === 'Pending';
              const isDelete = req.type === 'delete';
              return (
                <div key={req._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                  style={isPending ? { borderLeftWidth: 4, borderLeftColor: isDelete ? '#ef4444' : '#F0B429' } : {}}>
                  <div className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-bold text-gray-800">
                          {req.studentName} <span className="text-xs font-normal text-gray-400">({req.studentId}) · class on {req.classDate}</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Requested by <b className="text-gray-700">{req.requestedByName}</b>
                          {req.requestedByRole === 'Student Coordinator' ? ' (Student Coordinator)' : ' (Mentor)'} · {fmtWhen(req.createdAt)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-xs font-bold px-2.5 py-1 rounded-lg"
                          style={isDelete ? { background: '#fee2e2', color: '#b91c1c' } : { background: '#ede9fe', color: '#5b21b6' }}>
                          {isDelete ? '🗑 Delete request' : '✏️ Edit request'}
                        </span>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-lg" style={{ background: st.bg, color: st.color }}>{req.status}</span>
                      </div>
                    </div>

                    <div className="rounded-xl px-3 py-2 text-sm bg-gray-50 text-gray-700">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wide mr-1.5">Reason:</span>{req.reason}
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-gray-100">
                      {isDelete ? (
                        // Snapshot of the class taken when the delete was requested.
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="bg-red-50">
                              <th colSpan={2} className="px-3 py-2 text-left text-xs font-semibold text-red-700 uppercase tracking-wide">
                                Class log to be deleted
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.keys(FIELD_LABELS).map(field => (
                              <tr key={field} className="border-t border-gray-100">
                                <td className="px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap w-32">{FIELD_LABELS[field]}</td>
                                <td className={`px-3 py-2 ${req.status === 'Approved' ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{req.original[field] || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="bg-gray-50">
                              {['Field', 'Current', 'Requested'].map(h => (
                                <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(req.changes).map(([field, value]) => (
                              <tr key={field} className="border-t border-gray-100">
                                <td className="px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">{FIELD_LABELS[field] ?? field}</td>
                                <td className="px-3 py-2 text-gray-400 line-through">{req.original[field] || '—'}</td>
                                <td className="px-3 py-2 font-semibold text-purple-900">{value || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {isPending ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input value={notes[req._id] ?? ''} onChange={e => setNotes(n => ({ ...n, [req._id]: e.target.value }))}
                          placeholder="Note to mentor (optional)"
                          className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />
                        <div className="flex gap-2">
                          <button onClick={() => review(req, 'Rejected')} disabled={busy === req._id}
                            className="px-4 py-2 rounded-xl text-xs font-bold border-2 disabled:opacity-50"
                            style={{ borderColor: '#ef4444', color: '#b91c1c' }}>
                            ✕ Reject
                          </button>
                          <button onClick={() => review(req, 'Approved')} disabled={busy === req._id}
                            className="px-4 py-2 rounded-xl text-xs font-bold text-white whitespace-nowrap disabled:opacity-50"
                            style={{ background: isDelete ? '#dc2626' : '#16a34a' }}>
                            {busy === req._id ? '…' : isDelete ? '🗑 Approve & Delete' : '✓ Approve'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">
                        {req.status} {req.reviewedAt ? `on ${fmtWhen(req.reviewedAt)}` : ''}
                        {req.reviewNote ? <> — <span className="text-gray-600">“{req.reviewNote}”</span></> : ''}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default ClassLogEdits;
